/**
 * The "AI calls out" form's defect report letter fields (ring R9): where the
 * AI's own letter goes while it is being tried, and the switch that sends it to
 * companies.
 *
 * Its own group, next to the automation's, because it answers one question -
 * "does a machine mail a stranger" - the way the automation group answers "does
 * a machine ring one". It ships the way the calls ship: the letter goes to the
 * test inbox, and nothing reaches a company until the owner switches it on.
 *
 * The draft and the patch live in `aiOutboundNotes.ts`; this file only draws
 * them. What is said about the switch is said from the SAVED value, because that
 * is how letters are being sent right now; what is said about the box is said
 * from the draft, because that is what the save will keep.
 */
import { useT } from "../../../../../i18n/I18nProvider";
import { Field, FieldGroup, Note, Pill, ToggleRow } from "../../panel/fields";
import { readReportInbox, type OutboundDraft } from "./aiOutboundNotes";

export function AiOutboundReportMailFields({
  draft,
  onChange,
  savedLive,
}: {
  draft: OutboundDraft;
  onChange: (next: OutboundDraft) => void;
  /** Whether letters go to companies now, as the server last said. */
  savedLive: boolean;
}) {
  const t = useT();
  const inbox = readReportInbox(draft.reportMailTestInbox);

  return (
    <FieldGroup title={t("settings.aiOutbound.reportMail.group")}>
      <Field label={t("settings.aiOutbound.reportMail.testInbox")} hint={t("settings.aiOutbound.reportMail.testInboxHint")}>
        <Pill
          className="h-10 max-w-[360px]"
          type="email"
          value={draft.reportMailTestInbox}
          onChange={(reportMailTestInbox) => onChange({ ...draft, reportMailTestInbox })}
          placeholder="test@webadmin.ee"
          autoComplete="off"
        />
      </Field>
      {/* Said before saving: an address that does not read as one is not sent. */}
      {inbox.invalid ? <Note tone="warn">{t("settings.aiOutbound.reportMail.testInboxInvalid")}</Note> : null}
      {!draft.reportMailLive && !inbox.value ? (
        <Note tone="info">{t("settings.aiOutbound.reportMail.noTestInbox")}</Note>
      ) : null}

      <ToggleRow
        label={t("settings.aiOutbound.reportMail.live")}
        hint={t("settings.aiOutbound.reportMail.liveHint")}
        checked={draft.reportMailLive}
        onChange={(reportMailLive) => onChange({ ...draft, reportMailLive })}
      />
      {savedLive ? <Note tone="warn">{t("settings.aiOutbound.reportMail.liveOn")}</Note> : null}
    </FieldGroup>
  );
}
